<script>
import { mapActions, mapState } from 'vuex';
import { cloneDeep } from 'lodash';
import axios from 'axios';
import mainViewTabMixin from '../../../mixins/mainViewTab';
import paginationMixin from '../../../mixins/pagination';
import MeasureListItem from './MeasureListItem';
import MeasureDialog from './MeasureDialog';
import TaskDialog from './TaskDialog';

export default {
  name: 'MeasureList',
  components: {
    MeasureListItem,
    MeasureDialog,
    TaskDialog,
  },
  mixins: [mainViewTabMixin, paginationMixin],
  props: {
    privacyAuditId: {
      type: Number,
      required: true,
    },
    path: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isInitialized: false,
      isMeasureDialogDisplayed: false,
      isTaskDialogDisplayed: false,
      headers: [{ text: 'Abgeschlossen', value: 'isFinished' }, { text: 'Bezeichnung', value: 'name' }, { text: 'Priorität', value: 'priority' },
        { text: 'Benutzer', value: 'username' }, { text: 'Aufgaben-Status', value: 'status' }, { text: 'Aktionen', value: '', sortable: false }],
      measureItem: {},
      task: {},
      localDisabled: false,
      measureItems: {},
      isFinishedFilter: null,
      isFinishedOptions: [{ id: null, text: 'alle' }, { id: true, text: 'ja' }, { id: false, text: 'nein' }],
    };
  },
  computed: {
    ...mapState(['privacyAuditDetails']),
    ...mapState('privacyAuditDetails', ['meta']),
    ...mapState('privacyAuditDetails', ['isRefreshed']),
    filteredItems() {
      const self = this;

      if (!this.measureItems) return [];
      if (this.isFinishedFilter == null) return this.measureItems;

      return this.measureItems.filter(it => it.isFinished === self.isFinishedFilter);
    },
  },
  watch: {
    isRefreshed(newVal) {
      if (newVal) {
        this.fetchData();
      }
    },
  },
  created() {
    this.paginationParams.privacyAuditId = this.privacyAuditId;
    this.localDisabled = this.disabled;
    this.measureItems = cloneDeep(this.privacyAuditDetails.measureData);
  },
  mounted() {
    this.fetchMeasures(this.privacyAuditId);
  },
  methods: {
    ...mapActions('privacyAuditDetails', ['setIsRefreshed']),
    fetchData() {
      this.fetchMeasures(this.privacyAuditId);
      this.setIsRefreshed(false);
    },
    showMeasureDialog(measureItemId) {
      axios.get(`/measureItem/${measureItemId}`)
        .then((measureItem) => {
          this.measureItem = measureItem.data;
          this.isMeasureDialogDisplayed = true;
        })
        .catch((err) => {
          console.log(err);
          this.isMeasureDialogDisplayed = false;
        });
    },
    showTaskDialog(taskId, measureItemId) {
      axios.get(`/measureItem/${measureItemId}`)
        .then((measureItem) => {
          this.measureItem = measureItem.data;

          if (taskId) {
            axios.get(`/task/${taskId}`)
              .then((task) => {
                this.task = task.data;
                this.isTaskDialogDisplayed = true;
              })
              .catch((err) => {
                console.log(err);
                this.isTaskDialogDisplayed = false;
              });
          } else {
            this.task = {};
            this.isTaskDialogDisplayed = true;
          }
        })
        .catch((err) => {
          console.log(err);
          this.isTaskDialogDisplayed = false;
        });
    },
    fetchTaskStatus(code) {
      let statusText;

      if (code === 0) {
        statusText = 'Offen';
      } else if (code === 1) {
        statusText = 'In Bearbeitung';
      } else if (code === 2) {
        statusText = 'Geschlossen';
      }

      return statusText;
    },
    fetchMeasures(privacyAuditId) {
      axios.get(`/privacyAudit/${privacyAuditId}/measureItem`, {
        params: null,
      })
        .then((measureItems) => {
          this.measureItems = measureItems.data;
        });
    },
  },
};
</script>

<template>
  <div class="measureItem-list">
    <template v-if="privacyAuditDetails.hasError">
      <p class="js-error-template ma-3">Failed to fetch measureItem list. Please try again!</p>
    </template>
    <template v-else>
      <v-toolbar dark dense height="40" color="blue-grey lighten-2">
        <v-tooltip bottom>
          <v-btn @click="fetchData" icon slot="activator">
            <v-icon>refresh</v-icon>
          </v-btn>
          <span>Aktualisieren</span>
        </v-tooltip>
      </v-toolbar>

      <template v-slot:extension>
        <v-flex xs4 md2>
          <v-select
            style="padding-left: 22px; padding-top: 25px;"
            :items="isFinishedOptions"
            v-model="isFinishedFilter"
            item-value="id"
            item-text="text"
            label="Abgeschlossen" />
        </v-flex>
      </template>

      <v-progress-linear
        v-if="privacyAuditDetails.isLoading && !isInitialized"
        :indeterminate="true"
        class="js-loading ma-0"
      />

      <v-data-table
        v-else
        :headers="headers"
        :items="filteredItems"
        :disable-initial-sort="true"
        :pagination="pagination"
        :loading="privacyAuditDetails.isLoading"
        :rows-per-page-items="rowsPerPageItems"
        :total-items="this.pagination.totalItems"
        :expand="true"
        item-key="id"
        @update:pagination="loadNextPage"
        rows-per-page-text="Zeilen pro Seite"
        no-data-text="keine Daten verfügbar"
        no-results-text="keine Einträge gefunden"
        class="elevation-1 js-table"
      >
        <template slot="items" slot-scope="props">
          <tr :style="'color:' +
            (fetchTaskStatus(props.item.task.status) == 'Geschlossen' ? '#26a73a' :
            (props.item.priority == 2 ? '#D32F2F' :
          (props.item.priority == 1 ? '#FFA000' : '#00000')))">
            <td>

              <div v-if="props.item.isFinished">
                <v-icon color="#26a73a">done</v-icon>
              </div>
              <div v-else>
                <v-icon color="#D32F2F">close</v-icon>
              </div>
            </td>
            <td>{{ props.item.name }}</td>
            <td>{{ props.item.priority == 3 ? 'sehr hoch' :
            (props.item.priority == 2 ? 'hoch' :
            (props.item.priority == 1 ? 'normal' : 'gering')) }}</td>
            <td>{{ props.item.user ? props.item.user.username : null }}</td>
            <td>{{ props.item.task ? fetchTaskStatus(props.item.task.status) : null }}</td>
            <td>
              <v-layout row wrap>
                <v-flex xs6 md6>
                  <v-tooltip bottom>
                    <v-btn
                      icon
                      slot="activator"
                      @click="showMeasureDialog(props.item.id)">
                      <v-icon>edit</v-icon>
                    </v-btn>
                    <span>Maßnahme bearbeiten</span>
                  </v-tooltip>
                </v-flex>

                <v-flex xs6 md6>
                  <v-tooltip bottom>
                    <v-btn
                      slot="activator"
                      icon
                      @click="showTaskDialog(
                      props.item.task ? props.item.task.id : null, props.item.id)"
                    >
                      <v-icon>list</v-icon>
                    </v-btn>
                    <span>Aufgabe bearbeiten</span>
                  </v-tooltip>
                </v-flex>
              </v-layout>
            </td>
          </tr>

        </template>
        <template slot="pageText" slot-scope="{ pageStart, pageStop }">
          von {{ pageStart }} bis {{ pageStop }}
        </template>
      </v-data-table>
    </template>

    <MeasureDialog
      v-if="isMeasureDialogDisplayed"
      :measure-item="measureItem"
      :disabled="localDisabled"
      @cancel="isMeasureDialogDisplayed = false"
      @update="fetchMeasures(privacyAuditId)"
    />

    <TaskDialog
      v-if="isTaskDialogDisplayed"
      :task="task"
      :measure-item="measureItem"
      :disabled="localDisabled"
      @cancel="isTaskDialogDisplayed = false"
      @update="fetchMeasures(privacyAuditId)"
    />

  </div>
</template>


<style lang="scss" scoped>
.variant-cell {
  width: 30px;

  i {
    cursor: pointer;
  }
}
</style>
